import JSZip from "jszip";
import { saveAs } from "file-saver";
import { inlineHtmlArtifacts } from "../../../../../common/html_artifact_store.es6";
import {
  CLM_STAGE,
  listClmPages,
  sceneTitle,
  buildClmConfigJson,
  buildClmZipName,
  validateClmState,
} from "./clmResolvers";
import { serializeContainer } from "./clmSerializers";
import { renderClmIndexHtml, CLM_LIBRARY_URL } from "./clmIndexTemplate";

// Browser-side CLM package builder (spec §3.5, D5): no Visor bundle, no
// server. Same cancel contract as core/visor/main.es6 _buildScormZipBlob —
// ExportModal's Discard dispatches "canceled" on document.body.

// Same retry ladder as core/visor/main.es6:388 fetchWithRetry — copied, not
// imported: nothing under _editor/ imports core/visor/main.es6 and a 12-line
// helper does not justify opening that edge.
async function fetchWithRetry(url, options, retries = 3) {
  const delays = [1000, 3000, 5000];
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error("HTTP error! status: " + response.status);
      return response;
    } catch (err) {
      if (err.name === "AbortError" || (options && options.signal && options.signal.aborted))
        throw err;
      if (i === retries - 1) throw err;
      await new Promise((resolve) => setTimeout(resolve, delays[i] || 3000));
    }
  }
  return null;
}

// Backgrounds land raw in a style="background:...\" attribute (clmIndexTemplate.js
// pageDiv/overlayDiv). Allowlisting real colours — the assembler only ever
// emits #hex or rgb()/rgba() — instead of blocklisting URL-ish prefixes closes
// a CSS-injection hole: a blocklist lets a string like
// "red;background-image:url(https://evil/x)" through because it doesn't
// START with http(s)/data:/url(, even though the semicolon breaks out of the
// declaration.
const SAFE_BACKGROUND_COLOR =
  /^(#[0-9a-fA-F]{3,8}|rgba?\(\d{1,3},\s?\d{1,3},\s?\d{1,3}(,\s?[\d.]+)?\))$/;
function isSafeBackgroundColor(value) {
  return SAFE_BACKGROUND_COLOR.test(String(value || "").trim());
}

// Separator scenes: the scene background colour is the page (ruling 3).
function containerBackground(viewToolbar, fallback) {
  const background = viewToolbar && viewToolbar.background;
  return background && isSafeBackgroundColor(background) ? background : fallback;
}

// Cancel sentinel: distinct from Problem-carrying rejections (validation/
// artifact/parity/downloadFailed) so a caller (ExportModal) can tell a
// user-initiated cancel apart from a real export failure and swallow it
// instead of surfacing a stale error on the next Export open.
function cancelledError() {
  const err = new Error("Export cancelled");
  err.code = "cancelled";
  return err;
}

export function buildClmZip(state, options = {}) {
  // validateClmState reads state.* without a top-level null-check (e.g. a
  // null/undefined state throws synchronously); guard it here so buildClmZip
  // always returns a promise, never throws — exportClmZip's .then/.catch
  // chain can't catch a synchronous throw.
  let validation;
  try {
    validation = validateClmState(state);
  } catch (err) {
    return Promise.reject({
      code: "validation",
      problems: [{ code: "invalid_state", detail: String((err && err.message) || err) }],
      warnings: [],
    });
  }
  if (validation.errors.length > 0) {
    return Promise.reject({
      code: "validation",
      problems: validation.errors,
      warnings: validation.warnings,
    });
  }

  const abortController = new AbortController();
  const cancelHandler = () => abortController.abort();
  document.body.addEventListener("canceled", cancelHandler, { once: true });
  const cleanup = () => document.body.removeEventListener("canceled", cancelHandler);
  const cancelled = () => abortController.signal.aborted;

  return inlineHtmlArtifacts(state)
    .catch((err) => {
      throw {
        code: "artifact",
        problems: [{ code: "artifact_failed", detail: String((err && err.message) || err) }],
      };
    })
    .then(async (resolved) => {
      if (cancelled()) throw cancelledError();
      const navItemsById = resolved.navItemsById || {};
      const viewToolbars = resolved.viewToolbarsById || {};
      const pageIds = listClmPages(resolved.navItemsIds, navItemsById);
      const warnings = validation.warnings.slice();
      const assets = [];
      const overlayIds = [];

      const pages = pageIds.map((pageId, index) => {
        const pageNumber = index + 1;
        const title = sceneTitle(pageId, navItemsById, viewToolbars);
        const out = serializeContainer({
          containerId: pageId,
          boxIds: navItemsById[pageId].boxes,
          originPageId: pageId,
          state: resolved,
          imageName: (ordinal, ext) =>
            "page-" + pageNumber + (ordinal === 1 ? "" : "-" + ordinal) + "." + ext,
        });
        out.assets.forEach((asset) => assets.push({ ...asset, scene: title }));
        out.overlayIds.forEach((id) => {
          if (overlayIds.indexOf(id) === -1) overlayIds.push(id);
        });
        out.problems.forEach((problem) => warnings.push({ ...problem, scene: title, pageNumber }));
        return {
          id: "page-" + pageNumber,
          html: out.html,
          background: containerBackground(viewToolbars[pageId], "#000000"),
        };
      });

      const overlays = overlayIds.map((cvId, index) => {
        const cv = (resolved.containedViewsById || {})[cvId];
        const title = (cv && cv.name) || cvId;
        const out = serializeContainer({
          containerId: cvId,
          boxIds: (cv && cv.boxes) || [],
          originPageId: null,
          state: resolved,
          imageName: (ordinal, ext) => "overlay-" + (index + 1) + "-" + ordinal + "." + ext,
        });
        out.assets.forEach((asset) => assets.push({ ...asset, scene: title }));
        out.problems.forEach((problem) => warnings.push({ ...problem, scene: title }));
        return {
          id: cvId,
          html: out.html,
          background: containerBackground(viewToolbars[cvId], "#ffffff"),
        };
      });

      const clm = resolved.globalConfig && resolved.globalConfig.clm;
      const config = buildClmConfigJson(
        pageIds,
        navItemsById,
        viewToolbars,
        clm,
        options.updatedAt || new Date().toISOString()
      );
      const indexHtml = renderClmIndexHtml({
        title: (resolved.globalConfig && resolved.globalConfig.title) || "CLM",
        pages,
        overlays,
        stage: CLM_STAGE,
        libraryUrl: CLM_LIBRARY_URL,
      });

      // Count parity (spec §3.5 blocking rule): config slides == .page divs ==
      // exported scenes. Deliberately does NOT check images/pageIds — a
      // separator scene legitimately has no image (ruling 3), so requiring
      // image-count parity too would fail on every valid deck that has one.
      // Do not "fix" this into a 4-way check.
      const pageDivs = (indexHtml.match(/<div class="page( active)?" id="page-\d+"/g) || []).length;
      if (config.slides.length !== pageIds.length || pageDivs !== pageIds.length) {
        throw {
          code: "parity",
          problems: [
            {
              code: "parity",
              detail: config.slides.length + "/" + pageDivs + "/" + pageIds.length,
            },
          ],
        };
      }

      const zip = new JSZip();
      zip.file("index.html", indexHtml);
      zip.file("clm-config/config.json", JSON.stringify(config, null, 2));

      // ponytail: sequential fetches — a deck is a few dozen images; batch like
      // includeImage (main.es6:1365) only if export time ever matters.
      const failed = [];
      for (let i = 0; i < assets.length; i++) {
        if (cancelled()) throw cancelledError();
        const asset = assets[i];
        try {
          const response = await fetchWithRetry(asset.url, { signal: abortController.signal });
          zip.file("images/" + asset.fileName, await response.arrayBuffer(), { binary: true });
        } catch (err) {
          if ((err && err.name === "AbortError") || cancelled()) throw cancelledError();
          failed.push({
            code: "download_failed",
            scene: asset.scene,
            url: asset.url,
            detail: asset.fileName,
          });
        }
      }
      if (failed.length > 0) throw { code: "downloadFailed", problems: failed };

      const blob = await zip.generateAsync({ type: "blob" });
      if (cancelled()) throw cancelledError();
      return {
        zip,
        blob,
        name: options.zipName || buildClmZipName(resolved.globalConfig),
        warnings,
        config,
      };
    })
    .finally(cleanup);
}

export function exportClmZip(state, zipName, callback) {
  buildClmZip(state, { zipName })
    .then((result) => {
      saveAs(result.blob, result.name);
      callback(null, result);
    })
    .catch((err) => callback(err || new Error("CLM export failed")));
}
